import React, { useState, useEffect, useLayoutEffect } from "react";
import _ from "lodash";
import PlaybackFooterControls from "./components/PlaybackFooterControls";
import PlaybackHeader from "./components/PlaybackHeader";
import PlaybackContent from "./components/PlaybackContent";
import DraggableAndResizableDiv from "./components/DraggableAndResizableDiv";
import {
  createDocumentContent,
  getDocumentParts,
} from "./modules/GoogleDocsPlaybackViewerHelpers";

const GoogleDocsPlaybackViewer = ({
  name,
  isViewerVisible,
  closePlaybackViewer,
  isEmptyDocument,
  steps,
  userMap,
  playbackButtonPositionStyle,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState("10");
  const [userId, setUserId] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [finalStats, setFinalStats] = useState({});
  const [finalContent, setFinalContent] = useState("");
  const [documentParts, setDocumentParts] = useState([]);
  const [currentStats, setCurrentStats] = useState({});
  const [box, setBox] = useState({
    x: 0,
    y: 0,
    width: 620,
    height: 480,
    isDragging: false,
    isResizing: false,
    resizeDir: null,
    offsetX: 0,
    offsetY: 0,
    isInitialized: false,
  });

  const handlePlaybackSpeedChange = option => {
    setPlaybackSpeed(option.key);
  };

  const resetCurrentState = () => {
    setCurrentStep(0);
    setAutoPlay(false);
    setUserId("");
    setCurrentStats({});
    setDocumentParts([]);
  };

  useLayoutEffect(() => {
    if (isEmptyDocument) {
      resetCurrentState();
      return;
    }
    let content = "";
    let timeTaken = 0;
    let pasteCount = 0;

    for (let i = 0; i < steps.length; i++) {
      const entry = steps[i];
      timeTaken += entry?.timeTaken || 0;
      pasteCount += entry?.type === "paste" ? 1 : 0;
      content = createDocumentContent({ text: content, entry });
    }
    setFinalContent(content);
    setFinalStats({
      timeTaken,
      pasteCount,
      lastEdited: _.last(steps)?.timestamp,
      totalEdits: steps.length,
    });
  }, [steps, isEmptyDocument]);

  //Playback step
  useEffect(() => {
    if (isEmptyDocument || currentStep < 1) {
      resetCurrentState();
      return;
    }

    let content = "";
    let timeTaken = 0;
    let pasteCount = 0;

    for (let i = 0; i < currentStep - 1; i++) {
      const entry = steps[i];
      pasteCount += entry?.type === "paste" ? 1 : 0;
      timeTaken += entry?.timeTaken || 0;
      content = createDocumentContent({ text: content, entry });
    }

    const currentEntry = steps[currentStep - 1];
    pasteCount += currentEntry?.type === "paste" ? 1 : 0;
    const parts = getDocumentParts({ text: content, entry: currentEntry });
    setUserId(currentEntry.userId);

    timeTaken += currentEntry?.timeTaken || 0;
    setCurrentStats({
      timeTaken,
      pasteCount: pasteCount,
      lastEdited: currentEntry?.timestamp,
      totalEdits: currentStep,
    });
    setDocumentParts(parts);
  }, [currentStep, steps, isEmptyDocument]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay || isDragging || isEmptyDocument) return;

    const getDelay = () => {
      const base = 1000 / parseFloat(playbackSpeed);
      const idx = currentStep - 1;

      if (idx < 0 || idx >= steps.length) return base;
      const curr = steps[idx];
      const prev = idx > 0 ? steps[idx - 1] : null;
      const isBigInsertOrPaste = e =>
        e && (e.type === "insert" || e.type === "paste") && e.s.length > 50;

      const isBigDelete = e => e && e.type === "delete" && e.ei - e.si > 50;

      if (isBigInsertOrPaste(curr)) return base + 1000;
      if (isBigDelete(curr)) return base + 750;
      if (prev && (isBigInsertOrPaste(prev) || isBigDelete(prev)))
        return base + 500;

      return base;
    };

    const timeout = setTimeout(() => {
      if (!autoPlay || isDragging) return;
      setCurrentStep(prev => (prev >= steps.length ? prev : prev + 1));
    }, getDelay());

    return () => clearTimeout(timeout);
  }, [
    autoPlay,
    steps,
    playbackSpeed,
    currentStep,
    isDragging,
    isEmptyDocument,
  ]);

  useEffect(() => {
    if (!isViewerVisible) {
      setAutoPlay(false);
      setIsDragging(false);
    }
  }, [isViewerVisible]);

  useEffect(() => {
    if (playbackButtonPositionStyle && box?.isInitialized) {
      setBox(prev => ({ ...prev, isInitialized: false }));
    }
  }, [playbackButtonPositionStyle]);

  if (!isViewerVisible) {
    return null;
  }

  const stats = currentStep === 0 ? finalStats : currentStats;

  return (
    <DraggableAndResizableDiv
      box={box}
      setBox={setBox}
      minWidth={380}
      minHeight={240}
    >
      <PlaybackHeader
        name={name}
        stats={stats}
        isEmptyDocument={isEmptyDocument}
        closePlaybackViewer={closePlaybackViewer}
      />
      <PlaybackContent
        currentStep={currentStep}
        finalContent={finalContent}
        documentParts={documentParts}
        userMap={userMap}
        userId={userId}
      />
      <PlaybackFooterControls
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        autoPlay={autoPlay}
        setAutoPlay={setAutoPlay}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
        playbackSpeed={playbackSpeed}
        handlePlaybackSpeedChange={handlePlaybackSpeedChange}
        isEmptyDocument={isEmptyDocument}
        width={box.width}
      />
    </DraggableAndResizableDiv>
  );
};

export default GoogleDocsPlaybackViewer;
